import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!content) { el?.remove(); return; }
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.rel = "canonical"; document.head.appendChild(el); }
  el.href = href;
}

// Keeps title, description, canonical and social tags in sync with the current route (same data the server renders).
export function useSeoHead() {
  const { pathname } = useLocation();
  useEffect(() => {
    let live = true;
    setCanonical(`${window.location.origin}${pathname}`);
    api.get("/seo/meta", { params: { path: pathname } }).then(({ data: m }) => {
      if (!live) return;
      document.title = m.title;
      setCanonical(m.canonical || `${window.location.origin}${pathname}`);
      setMeta("name", "description", m.description);
      setMeta("property", "og:title", m.title);
      setMeta("property", "og:description", m.description);
      setMeta("property", "og:url", m.canonical);
      setMeta("property", "og:type", m.type);
      setMeta("property", "og:image", m.image);
      setMeta("name", "twitter:card", m.image ? "summary_large_image" : "summary");
      setMeta("name", "robots", m.noindex ? "noindex" : "");
    }).catch(() => {});
    return () => { live = false; };
  }, [pathname]);
}

// Google Analytics 4 with Consent Mode: loads immediately, stores cookies only after the visitor accepts.
export function useGa4(id, accepted) {
  useEffect(() => {
    if (!id || window.__ga4Loaded === id) return;
    window.__ga4Loaded = id;
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); }; // eslint-disable-line prefer-rest-params
    const state = accepted ? "granted" : "denied";
    window.gtag("consent", "default", { analytics_storage: state, ad_storage: state, ad_user_data: state, ad_personalization: state });
    window.gtag("js", new Date());
    window.gtag("config", id);
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(s);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (accepted && window.gtag) window.gtag("consent", "update", { analytics_storage: "granted", ad_storage: "granted", ad_user_data: "granted", ad_personalization: "granted" });
  }, [accepted]);
}
